import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/cn'
import { motion } from '@/utils/motion'
import { Flashcard } from './Flashcard'
import { fetchDueReviews, submitReview } from '@/api/reviews'
import { LoadingMessage } from '@/components/ui/LoadingMessage'
import { WordDetailModal } from '@/components/word/WordDetailModal'
import type { ReviewCard, ReviewRating } from '@/types/review'

interface ReviewSessionProps {
  languageId: number
  deckId?: number
  /** Random practice: this many cards from the whole vocabulary, each skippable. */
  randomCount?: number
  onClose: () => void
}

function cardKey(card: ReviewCard): string {
  return card.type === 'word' ? `w-${card.word.id}` : `c-${card.custom_flashcard.id}`
}

function completionMessage(total: number, practice: boolean): string {
  const cards = `${total} ${total === 1 ? 'card' : 'cards'}`
  if (practice) {
    return total === 0 ? 'There are no words to practise yet. Track a few while reading.' : `You practised ${cards}.`
  }
  return total === 0
    ? 'No cards are due for review right now. Keep reading and tracking words.'
    : `You reviewed ${cards}. Come back later for more.`
}

export function ReviewSession({ languageId, deckId, randomCount, onClose }: ReviewSessionProps) {
  const [cards, setCards] = useState<ReviewCard[]>([])
  const [index, setIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [detailsWordId, setDetailsWordId] = useState<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // The detail modal has its own Escape handler — let it close alone.
      if (e.key === 'Escape' && detailsWordId === null) onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, detailsWordId])

  useEffect(() => {
    fetchDueReviews(languageId, deckId, randomCount)
      .then((res) => {
        setCards(res.data.data)
        if (res.data.data.length === 0) setCompleted(true)
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false))
  }, [languageId, deckId, randomCount])

  // Moves past the current card once `work` settles; Skip passes no work, so
  // the card's strength is left untouched.
  const advance = useCallback((work?: Promise<unknown>) => {
    if (!cards[index] || submitting) return

    setSubmitting(true)
    Promise.resolve(work)
      .then(() => (cardRef.current ? motion.leave(cardRef.current) : undefined))
      .then(() => {
        const next = index + 1
        if (next >= cards.length) {
          setCompleted(true)
        } else {
          setIndex(next)
        }
      })
      .finally(() => setSubmitting(false))
  }, [cards, index, submitting])

  const handleRate = useCallback((rating: ReviewRating) => {
    const card = cards[index]
    if (card && !submitting) advance(submitReview(card, rating))
  }, [advance, cards, index, submitting])

  const handleSkip = useCallback(() => advance(), [advance])

  const practice = randomCount !== undefined

  const total = cards.length
  const current = Math.min(index + 1, total)
  const card = cards[index]

  // Each new card slides in; the progress line grows to the new position.
  useEffect(() => {
    if (cardRef.current) void motion.enter(cardRef.current)
    if (barRef.current && total > 0) void motion.grow(barRef.current, (current / total) * 100)
  }, [current, total])

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={practice ? 'Practice session' : 'Review session'}
    >
      {/*
        A wash of the page rather than a panel. The session used to be a bordered card
        holding the flashcard — a card inside a card, with the outer one competing for
        the same reading. Now the whole screen is the session and the flashcard is the
        only surface in it. The wash is near-opaque rather than a dim scrim so the text
        and buttons that sit directly on it have a background with known contrast.
      */}
      <div className="absolute inset-0 bg-page-bg/95 backdrop-blur-sm" aria-hidden="true" />

      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative flex flex-1 flex-col min-h-0 outline-none"
      >
        {/* Header — no rule beneath it; the card below supplies the only edge. It
            shares the card's column so the session reads as one stack rather than
            chrome pinned to the far corners of a wide screen. */}
        <div className="mx-auto flex w-full max-w-md items-center justify-between px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="font-serif text-lg font-medium text-heading">
              {practice ? 'Practice' : 'Review'}
            </h2>
            {!loading && !completed && total > 0 && (
              <span className="font-mono text-xs uppercase tracking-widest text-muted" aria-live="polite">
                Card {current} of {total}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className={cn(
              'text-muted hover:text-heading transition-colors duration-200',
              'min-h-11 min-w-11 flex items-center justify-center',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              'rounded-md',
            )}
            aria-label={practice ? 'Close practice session' : 'Close review session'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Progress — the width of the card it tracks, not of the screen. */}
        {!loading && total > 0 && (
          <div className="mx-auto w-full max-w-md px-4 sm:px-6" aria-hidden="true">
            <div className="h-0.5 rounded-full bg-line-soft">
              <div ref={barRef} className="h-full rounded-full bg-accent" style={{ width: 0 }} />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <LoadingMessage>Loading cards…</LoadingMessage>
            </div>
          )}

          {!loading && completed && (
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-heading mb-3">
                All done
              </h3>
              <p className="text-sm text-muted font-light leading-relaxed max-w-xs">
                {completionMessage(total, practice)}
              </p>
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'mt-8 px-8 py-3 rounded-full text-sm font-light tracking-wide',
                  'bg-btn-filled-bg text-btn-filled-text hover:bg-btn-filled-hover-bg',
                  'transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                  'min-h-11',
                )}
              >
                Back to dashboard
              </button>
            </div>
          )}

          {/*
            On a phone the card fills what is left so the rating row stays on the
            bottom edge, within thumb reach. On a desktop that stranded the ratings at
            the foot of a 900px screen, far from the card they belong to — so there the
            block takes its natural height and centres instead.
          */}
          {!loading && !completed && card && (
            <div ref={cardRef} className="flex-1 flex flex-col min-h-0 md:flex-none md:my-auto">
              <Flashcard
                key={cardKey(card)}
                card={card}
                onRate={handleRate}
                onSkip={practice ? handleSkip : undefined}
                onShowDetails={
                  card.type === 'word' ? () => setDetailsWordId(card.word.id) : undefined
                }
                disabled={submitting}
                keyboardEnabled={detailsWordId === null}
              />
            </div>
          )}
        </div>
      </div>

      {detailsWordId !== null && (
        <WordDetailModal
          languageId={languageId}
          wordId={detailsWordId}
          onClose={() => setDetailsWordId(null)}
        />
      )}
    </div>
  )
}
